// src/utils/barcodeHelper.js
// Client-side barcode and QR rendering to data URLs.
// Uses jsbarcode + qrcode-generator (already in KitchenPrint package.json)

import JsBarcode from 'jsbarcode'
import QRCode    from 'qrcode-generator'

// ── JsBarcode type map ───────────────────────────────────────
const JSBARCODE_FORMAT = {
  code128:    'CODE128',
  ean13:      'EAN13',
  ean8:       'EAN8',
  'gs1-128':  'GS1-128',
  datamatrix: null,   // not supported by JsBarcode — use placeholder
}

/**
 * Render a barcode to a PNG data URL using JsBarcode.
 */
export function renderBarcode(value, type = 'code128', opts = {}) {
  return new Promise((resolve, reject) => {
    const format = JSBARCODE_FORMAT[type.toLowerCase()]

    if (!format) {
      resolve(dataMatrixPlaceholder(value, opts.width || 60, opts.height || 60))
      return
    }

    const canvas = document.createElement('canvas')
    try {
      JsBarcode(canvas, value, {
        format,
        width:        2,
        height:       opts.height || 60,
        displayValue: opts.displayValue ?? false,
        margin:       2,
        background:   '#ffffff',
        lineColor:    '#000000',
      })
      resolve(canvas.toDataURL('image/png'))
    } catch (err) {
      reject(new Error(`Barcode render failed (${type}): ${err.message}`))
    }
  })
}

/**
 * Render a QR code to a PNG data URL using qrcode-generator.
 * Same library KitchenPrint already uses in LabelView.vue.
 */
export function renderQR(value, size = 120) {
  if (!value || value.startsWith('{')) {
    return Promise.resolve(qrPlaceholder(size))
  }

  return new Promise((resolve, reject) => {
    try {
      const qr = QRCode(0, 'M')
      qr.addData(value)
      qr.make()

      const moduleCount = qr.getModuleCount()
      const cellSize    = Math.max(2, Math.floor(size / moduleCount))
      const canvasSize  = moduleCount * cellSize

      const canvas = document.createElement('canvas')
      canvas.width  = canvasSize
      canvas.height = canvasSize

      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, canvasSize, canvasSize)
      ctx.fillStyle = '#000000'

      for (let row = 0; row < moduleCount; row++) {
        for (let col = 0; col < moduleCount; col++) {
          if (qr.isDark(row, col)) {
            ctx.fillRect(col * cellSize, row * cellSize, cellSize, cellSize)
          }
        }
      }

      resolve(canvas.toDataURL('image/png'))
    } catch (err) {
      reject(new Error(`QR render failed: ${err.message}`))
    }
  })
}

/**
 * Validate a barcode value for a given type.
 */
export function validateBarcode(value, type) {
  if (!value) return { valid: false, error: 'Value is required' }
  switch (type?.toLowerCase()) {
    case 'ean13':
      if (!/^\d{12,13}$/.test(value))
        return { valid: false, error: 'EAN-13 must be 12 or 13 digits' }
      break
    case 'ean8':
      if (!/^\d{7,8}$/.test(value))
        return { valid: false, error: 'EAN-8 must be 7 or 8 digits' }
      break
    case 'gs1-128':
      if (!/^\(?\d{2,4}\)?/.test(value))
        return { valid: false, error: 'GS1-128 must start with an Application Identifier e.g. (01)' }
      break
    case 'datamatrix':
      if (value.length > 2335)
        return { valid: false, error: 'DataMatrix value too long (max 2335 chars)' }
      break
  }
  return { valid: true }
}

// ── Placeholders ─────────────────────────────────────────────

function dataMatrixPlaceholder(value, w, h) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
    <rect width="${w}" height="${h}" fill="white" stroke="#ccc"/>
    <text x="50%" y="50%" font-size="10" text-anchor="middle"
          dominant-baseline="middle" fill="#888">[DataMatrix]</text>
    <text x="50%" y="70%" font-size="8" text-anchor="middle"
          dominant-baseline="middle" fill="#aaa">${value.slice(0, 12)}</text>
  </svg>`
  return `data:image/svg+xml;base64,${btoa(svg)}`
}

function qrPlaceholder(size) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
    <rect width="${size}" height="${size}" fill="white" stroke="#ccc"/>
    <text x="50%" y="50%" font-size="${size * 0.12}" text-anchor="middle"
          dominant-baseline="middle" fill="#888">[QR]</text>
  </svg>`
  return `data:image/svg+xml;base64,${btoa(svg)}`
}

export const BARCODE_TYPES = [
  { value: 'code128',    label: 'Code 128',   group: 'Linear' },
  { value: 'ean13',      label: 'EAN-13',      group: 'Linear' },
  { value: 'ean8',       label: 'EAN-8',       group: 'Linear' },
  { value: 'gs1-128',    label: 'GS1-128',     group: 'GS1 / Compliance' },
  { value: 'datamatrix', label: 'Data Matrix', group: 'GS1 / Compliance' },
  { value: 'qrcode',     label: 'QR Code',     group: '2D' },
]